import { create } from "zustand";
import { getEventData } from "../services/event";

// legacy rows stored *_position double-encoded, so parse strings back to arrays
const parsePositionList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return value;
  try {
    return parsePositionList(JSON.parse(value));
  } catch {
    return value;
  }
};

const normalizeEventData = (event) => {
  if (!event || typeof event !== "object") return event;
  return {
    ...event,
    ticket_position: parsePositionList(event.ticket_position),
    speaker_position: parsePositionList(event.speaker_position),
    sponsor_position: parsePositionList(event.sponsor_position),
  };
};

export const useSite = create((set) => ({
  //login in site
  user: null,
  setUser: (updater) =>
    set((state) => ({
      user: typeof updater === "function" ? updater(state.user) : updater,
    })),

  // site show - hide nav/links/button data
  siteHideData: null,
  setSiteHideData: (updater) =>
    set((state) => ({
      siteHideData:
        typeof updater === "function" ? updater(state.siteHideData) : updater,
    })),

  //site theme
  siteTheme: null,
  setSiteTheme: (updater) =>
    set((state) => ({
      siteTheme:
        typeof updater === "function" ? updater(state.siteTheme) : updater,
    })),

  //site user
  siteUser: null,
  isExhibitor: false,
  isExhibitorSpace: false,
  setSiteUser: (updater) =>
    set((state) => {
      const newUser = typeof updater === "function" ? updater(state.siteUser) : updater;
      
      if (!newUser) {
        return {
          siteUser: null,
          isExhibitor: false,
          isExhibitorSpace: false,
          siteUserLoading: false,
        };
      }

      const isExhibitor = newUser?.role_id === 23 || newUser?.role_id === 24 || newUser?.role_id === 25;
      const isExhibitorSpace = newUser?.exhibitor_category?.has_features === true;
      
      return {
        siteUser: newUser,
        isExhibitor,
        isExhibitorSpace,
        siteUserLoading: false,
      };
    }),

  // site layout data
  siteData: null,
  setSiteData: (updater) =>
    set((state) => ({
      siteData:
        typeof updater === "function" ? updater(state.siteData) : updater,
    })),

  // site style data
  siteStyle: null,
  setSiteStyle: (updater) =>
    set((state) => ({
      siteStyle:
        typeof updater === "function" ? updater(state.siteStyle) : updater,
    })),

  siteId: null,
  setSiteId: (updater) =>
    set((state) => ({
      siteId: typeof updater === "function" ? updater(state.siteId) : updater,
    })),

  // site social data from site layout data
  socialData: null,
  setSocialData: (updater) =>
    set((state) => ({
      socialData:
        typeof updater === "function" ? updater(state.socialData) : updater,
    })),

  // site footer links terms and Policies
  linksData: null,
  setLinksData: (updater) =>
    set((state) => ({
      linksData:
        typeof updater === "function" ? updater(state.linksData) : updater,
    })),

  // site mode data like edit or view
  mode: "view",
  setMode: (updater) =>
    set((state) => ({
      mode: typeof updater === "function" ? updater(state.mode) : updater,
    })),

  // event data
  eventData: null,
  setEventData: (updater) =>
    set((state) => ({
      eventData: normalizeEventData(
        typeof updater === "function" ? updater(state.eventData) : updater,
      ),
    })),

  // why data
  whyData: null,
  setWhyData: (updater) =>
    set((state) => ({
      whyData: typeof updater === "function" ? updater(state.whyData) : updater,
    })),

  // cards section data
  cardsData: null,
  setCardsData: (updater) =>
    set((state) => ({
      cardsData:
        typeof updater === "function" ? updater(state.cardsData) : updater,
    })),

  // speaker data
  speakerData: null,
  setSpeakerData: (updater) =>
    set((state) => ({
      speakerData:
        typeof updater === "function" ? updater(state.speakerData) : updater,
    })),

  // sponsor data
  sponsorData: null,
  setSponsorData: (updater) =>
    set((state) => ({
      sponsorData:
        typeof updater === "function" ? updater(state.sponsorData) : updater,
    })),

  // venue data
  venueData: null,
  setVenueData: (updater) =>
    set((state) => ({
      venueData:
        typeof updater === "function" ? updater(state.venueData) : updater,
    })),

  // event data
  agendaData: null,
  setAgendaData: (updater) =>
    set((state) => ({
      agendaData:
        typeof updater === "function" ? updater(state.agendaData) : updater,
    })),

  // edit side bar (editor)
  sidebarStatus: null,
  setSidebarStatus: (updater) =>
    set((state) => ({
      sidebarStatus:
        typeof updater === "function" ? updater(state.sidebarStatus) : updater,
    })),

  // cookies status
  cookieStatus: null,
  setCookieStatus: (updater) =>
    set((state) => ({
      cookieStatus:
        typeof updater === "function" ? updater(state.cookieStatus) : updater,
    })),

  // site dashboard profile setting popup status
  profileSettingPopup: null,
  setProfileSettingPopup: (updater) =>
    set((state) => ({
      profileSettingPopup:
        typeof updater === "function"
          ? updater(state.profileSettingPopup)
          : updater,
    })),

  // ticket theme
  ticketTheme: null,
  setTicketTheme: (updater) =>
    set((state) => ({
      ticketTheme:
        typeof updater === "function" ? updater(state.ticketTheme) : updater,
    })),

  ticketThemeMobile: null,
  setTicketThemeMobile: (updater) =>
    set((state) => ({
      ticketThemeMobile:
        typeof updater === "function"
          ? updater(state.ticketThemeMobile)
          : updater,
    })),

  ticketThemeDesktop: null,
  setTicketThemeDesktop: (updater) =>
    set((state) => ({
      ticketThemeDesktop:
        typeof updater === "function"
          ? updater(state.ticketThemeDesktop)
          : updater,
    })),

  ticketHeaderTheme: null,
  setTicketHeaderTheme: (updater) =>
    set((state) => ({
      ticketHeaderTheme:
        typeof updater === "function"
          ? updater(state.ticketHeaderTheme)
          : updater,
    })),

  ticketHeaderThemeMobile: null,
  setTicketHeaderThemeMobile: (updater) =>
    set((state) => ({
      ticketHeaderThemeMobile:
        typeof updater === "function"
          ? updater(state.ticketHeaderThemeMobile)
          : updater,
    })),

  ticketHeaderThemeDesktop: null,
  setTicketHeaderThemeDesktop: (updater) =>
    set((state) => ({
      ticketHeaderThemeDesktop:
        typeof updater === "function"
          ? updater(state.ticketHeaderThemeDesktop)
          : updater,
    })),

  // modal mode: 'auto' (login trigger) vs 'edit' (manual header click)
  profileModalMode: "auto",
  setProfileModalMode: (updater) =>
    set((state) => ({
      profileModalMode:
        typeof updater === "function" ? updater(state.profileModalMode) : updater,
    })),

  siteUserLoading: true,
  setSiteUserLoading: (updater) =>
    set((state) => ({
      siteUserLoading:
        typeof updater === "function" ? updater(state.siteUserLoading) : updater,
    })),
}));

const eventPromises = new Map();

export const fetchEventInfo = async (eventId) => {
  if (!eventId) return null;
  const key = `${eventId}`;
  if (eventPromises.has(key)) {
    return eventPromises.get(key);
  }
  const promise = (async () => {
    try {
      const res = await getEventData(eventId);
      return res.data?.data?.event || null;
    } catch (e) {
      return null;
    } finally {
      eventPromises.delete(key);
    }
  })();
  eventPromises.set(key, promise);
  return promise;
};
